import { useEffect } from 'react';

import { DEFAULT_DESCRIPTION, OG_IMAGE_URL, SITE_NAME } from '../config/site';
import { hasSsrMeta } from '../utils/ssrBootstrap';

function upsertMeta(selector, attrs) {
  let el = document.querySelector(selector);
  const created = !el;
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  const prev = {};
  for (const [key, value] of Object.entries(attrs)) {
    prev[key] = el.getAttribute(key);
    el.setAttribute(key, value);
  }
  return { el, created, prev };
}

/**
 * @param {{ title: string, description?: string, canonicalPath?: string }} meta
 */
export function usePageMeta({ title, description, canonicalPath }) {
  useEffect(() => {
    if (hasSsrMeta()) return undefined;

    const prevTitle = document.title;
    document.title = title;

    const pageDescription = description || DEFAULT_DESCRIPTION;
    const canonicalUrl = canonicalPath
      ? `${window.location.origin}${canonicalPath}`
      : window.location.href;

    let metaDescription = document.querySelector('meta[name="description"]');
    const descCreated = !metaDescription;
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    const prevDescription = metaDescription.getAttribute('content');
    metaDescription.setAttribute('content', pageDescription);

    const socialTags = [
      upsertMeta('meta[property="og:title"]', {
        property: 'og:title',
        content: title,
      }),
      upsertMeta('meta[property="og:description"]', {
        property: 'og:description',
        content: pageDescription,
      }),
      upsertMeta('meta[property="og:url"]', {
        property: 'og:url',
        content: canonicalUrl,
      }),
      upsertMeta('meta[property="og:image"]', {
        property: 'og:image',
        content: OG_IMAGE_URL,
      }),
      upsertMeta('meta[property="og:site_name"]', {
        property: 'og:site_name',
        content: SITE_NAME,
      }),
      upsertMeta('meta[name="twitter:title"]', {
        name: 'twitter:title',
        content: title,
      }),
      upsertMeta('meta[name="twitter:description"]', {
        name: 'twitter:description',
        content: pageDescription,
      }),
      upsertMeta('meta[name="twitter:image"]', {
        name: 'twitter:image',
        content: OG_IMAGE_URL,
      }),
    ];

    let canonical = document.querySelector('link[rel="canonical"]');
    const canonicalCreated = !canonical;
    if (canonicalPath) {
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', canonicalUrl);
    }

    return () => {
      document.title = prevTitle;
      if (prevDescription !== null) {
        metaDescription.setAttribute('content', prevDescription || '');
      }
      if (descCreated && metaDescription.parentNode) {
        metaDescription.parentNode.removeChild(metaDescription);
      }
      for (const { el, created, prev } of socialTags) {
        if (created && el.parentNode) {
          el.parentNode.removeChild(el);
          continue;
        }
        for (const [key, value] of Object.entries(prev)) {
          if (value === null) el.removeAttribute(key);
          else el.setAttribute(key, value);
        }
      }
      if (canonicalCreated && canonical?.parentNode) {
        canonical.parentNode.removeChild(canonical);
      }
    };
  }, [title, description, canonicalPath]);
}
